// src/graphql/client.ts
var GraphQLRequestError = class extends Error {
  constructor(message, errors = [], status = 0) {
    super(message);
    this.errors = errors;
    this.status = status;
    this.name = "GraphQLRequestError";
  }
  errors;
  status;
};
var DEFAULT_TIMEOUT_MS = 15e3;
var GraphQLClient = class {
  config;
  doFetch;
  constructor(config) {
    this.config = config;
    const impl = config.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : void 0);
    if (!impl) throw new Error("GraphQLClient: no fetch implementation available");
    this.doFetch = impl.bind(globalThis);
  }
  /**
   * Execute one GraphQL operation and return its typed `data`. Throws
   * {@link GraphQLRequestError} on any transport failure or GraphQL `errors[]`.
   */
  async request(query, variables, opts = {}) {
    const endpoint = typeof this.config.endpoint === "function" ? this.config.endpoint() : this.config.endpoint;
    if (!endpoint) {
      throw new GraphQLRequestError("GraphQL endpoint is not resolvable", [], 0);
    }
    const token = await Promise.resolve(this.config.getAccessToken());
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
    if (token) headers.Authorization = token;
    if (this.config.requestSigner) {
      Object.assign(headers, await Promise.resolve(this.config.requestSigner()));
    }
    const timeoutMs = opts.timeoutMs ?? this.config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const onAbort = () => ctrl.abort();
    opts.signal?.addEventListener("abort", onAbort, { once: true });
    let response;
    try {
      response = await this.doFetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({ query, variables: variables ?? {} }),
        signal: ctrl.signal
      });
    } catch (err) {
      throw new GraphQLRequestError(
        err instanceof Error ? err.message : "GraphQL request failed",
        [],
        0
      );
    } finally {
      clearTimeout(timer);
      opts.signal?.removeEventListener("abort", onAbort);
    }
    if (!response.ok) {
      const body2 = await safeJson(response);
      const errors2 = extractErrors(body2);
      throw new GraphQLRequestError(
        errors2[0]?.message ?? `GraphQL request failed with status ${response.status}`,
        errors2,
        response.status
      );
    }
    const body = await safeJson(response);
    const errors = extractErrors(body);
    const firstError = errors[0];
    if (firstError) {
      throw new GraphQLRequestError(firstError.message, errors, response.status);
    }
    if (!body || body.data == null) {
      throw new GraphQLRequestError("GraphQL response had no data", [], response.status);
    }
    return body.data;
  }
};
async function safeJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
function extractErrors(body) {
  if (body && typeof body === "object" && Array.isArray(body.errors)) {
    return body.errors;
  }
  return [];
}

// src/graphql/operations/catalog.ts
var KIND_TO_GRAPHQL = {
  tag: "TAG",
  label: "LABEL",
  entity: "ENTITY",
  group: "GROUP"
};
function catalogKindToGraphql(kind) {
  return KIND_TO_GRAPHQL[kind];
}
var CATALOG_ITEMS_QUERY = (
  /* GraphQL */
  `
  query CatalogItems(
    $kind: CatalogKind!
    $limit: Int
    $includeDefaults: Boolean
    $search: String
    $cursor: String
  ) {
    catalogItems(
      kind: $kind
      limit: $limit
      includeDefaults: $includeDefaults
      search: $search
      cursor: $cursor
    ) {
      items {
        id
        kind
        name
        description
        color
        icon
        logo
        category
        entityType
        website
        twitter
        riskLevel
        severity
        lifecycle
        confidence
        explanation
        systemDefault
        aliases
        address
        chain
        chains
        primaryChains
        groupType
        sagaKind
        state
        memberCount
      }
      nextCursor
      hasMore
      approxTotal
      approxTotalCapped
    }
  }
`
);
var clean = (v) => v == null ? void 0 : v;
function mapCatalogItemToRow(item) {
  return {
    id: item.id,
    name: clean(item.name),
    description: clean(item.description),
    color: clean(item.color),
    // tag/label/group carry the logo under `icon`; entity/address under
    // `logo_s3_uri` — the unified GraphQL `logo` feeds the latter.
    icon: clean(item.icon),
    logo_s3_uri: clean(item.logo),
    category: clean(item.category),
    // The entity's identity, under the snake_case names `rowToDraft` reads. Without
    // these the editor drafts an entity with the type, site and handle blank — and
    // `draftToRowBody` then omits from the save what the form never showed. The
    // fields are all `rowToDraft` looks at, so this is the whole fix client-side.
    entity_type: clean(item.entityType),
    website: clean(item.website),
    twitter: clean(item.twitter),
    risk_level: clean(item.riskLevel),
    severity: clean(item.severity),
    lifecycle: clean(item.lifecycle),
    confidence: clean(item.confidence),
    explanation: item.explanation ?? [],
    system_default: item.systemDefault,
    aliases: item.aliases ?? [],
    // Address-scoped rows (address labels) carry their binding; the REST list
    // returns these too, so the transport-swapped row stays shape-identical.
    address: clean(item.address),
    chain: clean(item.chain),
    // Applicability scope so the list chip ("Applies to chains" / "All chains")
    // survives a refetch. The REST list carries this as `applies_to_chains`; the
    // adapter normalises it onto the same `chains` field so both transports emit
    // a shape-identical row.
    chains: item.chains ?? [],
    // Presentation hint so the editor re-shows a saved "Primary chains" pick
    // after a reload (REST returns `primary_chains` directly; `rowToDraft` reads
    // this snake_case field).
    primary_chains: item.primaryChains ?? [],
    // The merged group view. A cross-chain saga the grouping engine found reaches
    // the list beside the groups a person curated, and `group_type` is how the
    // row renders as one rather than as a curated group with a broken name. The
    // saga fields are what the badge, leg count and state chip read; REST returns
    // them under exactly these names, so a transport swap changes nothing.
    group_type: clean(item.groupType),
    saga_kind: clean(item.sagaKind),
    state: clean(item.state),
    member_count: clean(item.memberCount)
  };
}
function mapCatalogItems(data) {
  const page = data.catalogItems;
  return {
    items: (page?.items ?? []).map(mapCatalogItemToRow),
    nextCursor: page?.nextCursor ?? void 0,
    hasMore: page?.hasMore ?? void 0,
    total: page?.approxTotal ?? void 0,
    totalCapped: page?.approxTotalCapped ?? void 0
  };
}
async function fetchCatalogItems(client, kind, query = {}, opts) {
  const graphqlKind = catalogKindToGraphql(kind);
  if (!graphqlKind) throw new Error(`catalog kind "${kind}" is not served by GraphQL`);
  const data = await client.request(
    CATALOG_ITEMS_QUERY,
    {
      kind: graphqlKind,
      limit: query.limit,
      includeDefaults: query.includeDefaults ?? true,
      search: query.search,
      cursor: query.cursor
    },
    opts
  );
  return mapCatalogItems(data);
}

// src/graphql/operations/related.ts
var RELATED_SEED_KINDS = [
  "tag",
  "label",
  "entity",
  "group",
  "transaction_group"
];
var CATALOG_RELATED_QUERY = (
  /* GraphQL */
  `
  query CatalogRelated($kind: RelatedKind!, $id: ID!, $depth: Int, $include: [RelatedKind!]) {
    catalogRelated(kind: $kind, id: $id, depth: $depth, include: $include) {
      seed {
        kind
        id
        chainId
      }
      nodes {
        ref {
          kind
          id
          chainId
        }
        name
        relation
        via {
          kind
          id
          chainId
        }
        depth
      }
      edges {
        source {
          kind
          id
          chainId
        }
        target {
          kind
          id
          chainId
        }
        relation
      }
      counts {
        kind
        count
      }
      depth
      truncated
      partial
    }
  }
`
);
var toEnum = (kind) => kind.toUpperCase();
var fromEnum = (value) => value.toLowerCase();
function mapRef(ref) {
  return {
    kind: fromEnum(ref.kind),
    id: ref.id,
    ...ref.chainId ? { chainId: ref.chainId } : {}
  };
}
function mapRelatedGraph(data) {
  const g = data.catalogRelated;
  if (!g) return null;
  const counts = {};
  for (const { kind, count } of g.counts ?? []) {
    counts[fromEnum(kind)] = count;
  }
  return {
    seed: mapRef(g.seed),
    nodes: (g.nodes ?? []).map((n) => ({
      ref: mapRef(n.ref),
      ...n.name ? { name: n.name } : {},
      ...n.relation ? { relation: fromEnum(n.relation) } : {},
      ...n.via ? { via: mapRef(n.via) } : {},
      depth: n.depth
    })),
    edges: (g.edges ?? []).map((e) => ({
      source: mapRef(e.source),
      target: mapRef(e.target),
      relation: fromEnum(e.relation)
    })),
    counts,
    depth: g.depth,
    truncated: g.truncated,
    partial: g.partial
  };
}
async function fetchCatalogRelated(client, kind, id, args = {}, opts) {
  const data = await client.request(
    CATALOG_RELATED_QUERY,
    {
      kind: toEnum(kind),
      id,
      depth: args.depth,
      include: args.include?.map(toEnum)
    },
    opts
  );
  return mapRelatedGraph(data);
}
function relatedChildren(graph, ref) {
  const byRelation = /* @__PURE__ */ new Map();
  for (const edge of graph.edges) {
    if (edge.source.kind !== ref.kind || edge.source.id !== ref.id) continue;
    const list = byRelation.get(edge.relation) ?? [];
    list.push(edge.target);
    byRelation.set(edge.relation, list);
  }
  return [...byRelation.entries()].map(([relation, targets]) => ({ relation, targets }));
}
function relatedOfKind(graph, kind) {
  return graph.nodes.filter((n) => n.ref.kind === kind && n.depth > 0);
}

// src/graphql/operations/address.ts
var ADDRESS_SCOPES = ["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"];
function toAddressScope(scope) {
  const upper = scope.toUpperCase();
  return ADDRESS_SCOPES.includes(upper) ? upper : void 0;
}
var RESOLVED_ENTITY_FIELDS = (
  /* GraphQL */
  `
  id
  name
  type
  logo
  color
  confidence
  verified
`
);
var RESOLVED_HIT_CORE_FIELDS = (
  /* GraphQL */
  `
  kind
  catalogId
  name
  color
  logo
  tierWeight
  isPrimary
  severity
  riskLevel
  confidence
  lifecycle
  explanation
`
);
var RESOLVED_HIT_FIELDS = (
  /* GraphQL */
  `
  ${RESOLVED_HIT_CORE_FIELDS}
  availableScopes {
    scope
    tierWeight
    overrides {
      key
      value
    }
  }
`
);
function resolvedBundleFields(hitFields) {
  return (
    /* GraphQL */
    `
    address
    chainId
    asOf
    entities { ${RESOLVED_ENTITY_FIELDS} }
    tags { ${hitFields} }
    labels { ${hitFields} }
    groups { ${hitFields} }
    primary {
      kind
      catalogId
    }
  `
  );
}
var ADDRESS_OVERLAYS_QUERY = (
  /* GraphQL */
  `
  query AddressOverlays($address: String!, $chainId: String, $asOf: Int, $scope: AddressScope) {
    address(address: $address, chainId: $chainId, asOf: $asOf, scope: $scope) {
      ${resolvedBundleFields(RESOLVED_HIT_FIELDS)}
    }
  }
`
);
var clean2 = (v) => v == null ? void 0 : v;
function mapScope(scope) {
  const overrides = {};
  for (const { key, value } of scope.overrides ?? []) overrides[key] = value;
  return { scope: scope.scope, tierWeight: scope.tierWeight, overrides };
}
function mapHit(hit) {
  return {
    kind: hit.kind,
    catalogId: hit.catalogId,
    name: clean2(hit.name),
    color: clean2(hit.color),
    logo: clean2(hit.logo),
    tierWeight: hit.tierWeight,
    isPrimary: hit.isPrimary,
    severity: clean2(hit.severity),
    riskLevel: clean2(hit.riskLevel),
    confidence: clean2(hit.confidence),
    lifecycle: clean2(hit.lifecycle),
    explanation: hit.explanation ?? [],
    availableScopes: (hit.availableScopes ?? []).map(mapScope)
  };
}
function mapEntity(entity) {
  return {
    id: entity.id,
    name: clean2(entity.name),
    type: clean2(entity.type),
    logo: clean2(entity.logo),
    color: clean2(entity.color),
    confidence: clean2(entity.confidence),
    verified: entity.verified ?? void 0
  };
}
function mapBundle(a) {
  return {
    address: a.address,
    chainId: clean2(a.chainId),
    asOf: a.asOf,
    entities: (a.entities ?? []).map(mapEntity),
    tags: (a.tags ?? []).map(mapHit),
    labels: (a.labels ?? []).map(mapHit),
    groups: (a.groups ?? []).map(mapHit),
    primary: a.primary ?? []
  };
}
function mapAddressOverlays(data) {
  return data.address ? mapBundle(data.address) : null;
}
async function fetchAddressOverlays(client, args, opts) {
  const data = await client.request(
    ADDRESS_OVERLAYS_QUERY,
    { address: args.address, chainId: args.chainId, asOf: args.asOf, scope: args.scope },
    opts
  );
  return mapAddressOverlays(data);
}

// src/graphql/operations/addressDetail.ts
var ADDRESS_ROW_FIELDS = `
  address
  chain
  addressType
  isPrimaryChain
  name
  notes
  symbol
  decimals
  isContract
  deployer
  deployedAt
  lastSeenAt
  riskLevel
  severity
  lifecycle
  isHumanApproved
  enrichmentStatus
  primaryEntityId
  logoS3Uri
  aliases
  source
  confidence
  systemDefault
  metadata
`;
var ADDRESS_DETAIL_QUERY = (
  /* GraphQL */
  `
  query AddressDetail($address: String!, $chainId: String!, $asOf: Int, $scope: AddressScope, $withScopes: Boolean) {
    addressDetail(address: $address, chainId: $chainId, asOf: $asOf, scope: $scope, withScopes: $withScopes) {
      saved
      ownOverride
      row { ${ADDRESS_ROW_FIELDS} }
      otherScopes {
        scope
        readable
        count
        overrides { key value }
      }
    }
  }
`
);
function mapAddressRow(row) {
  return {
    address: row.address,
    chain: row.chain,
    address_type: row.addressType ?? void 0,
    is_primary_chain: Boolean(row.isPrimaryChain),
    name: row.name ?? null,
    notes: row.notes ?? null,
    symbol: row.symbol ?? null,
    decimals: row.decimals ?? null,
    is_contract: row.isContract ?? null,
    deployer: row.deployer ?? null,
    deployed_at: row.deployedAt ?? null,
    last_seen_at: row.lastSeenAt ?? null,
    risk_level: row.riskLevel ?? void 0,
    severity: row.severity ?? void 0,
    lifecycle: row.lifecycle ?? void 0,
    // The pair `isVerified`/`isBeingMapped` read. Projected here so the detail
    // panel judges a row the same way whichever mouth answered it.
    is_human_approved: Boolean(row.isHumanApproved),
    enrichment_status: row.enrichmentStatus ?? null,
    primary_entity_id: row.primaryEntityId ?? null,
    logo_s3_uri: row.logoS3Uri ?? null,
    aliases: row.aliases ?? [],
    source: row.source ?? null,
    confidence: row.confidence ?? void 0,
    system_default: Boolean(row.systemDefault),
    metadata: row.metadata ?? {}
  };
}
function mapAddressRowScope(scope) {
  const overrides = {};
  for (const { key, value } of scope.overrides ?? []) overrides[key] = value;
  return {
    scope: scope.scope,
    readable: Boolean(scope.readable),
    count: scope.count ?? 0,
    overrides
  };
}
function mapAddressDetail(data) {
  const d = data.addressDetail;
  if (!d) return null;
  return {
    address: mapAddressRow(d.row),
    saved: Boolean(d.saved),
    own_override: Boolean(d.ownOverride),
    // The catalog overlay bundle is fetched separately by the detail UI (the
    // `address` overlays op), so it is intentionally not selected here.
    catalog: void 0,
    other_scopes: (d.otherScopes ?? []).map(mapAddressRowScope)
  };
}
async function graphqlFetchAddressDetail(client, args, opts) {
  const data = await client.request(
    ADDRESS_DETAIL_QUERY,
    {
      address: args.address,
      chainId: args.chainId,
      asOf: args.asOf,
      scope: args.scope,
      withScopes: args.withScopes ?? false
    },
    opts
  );
  return mapAddressDetail(data);
}

// src/graphql/operations/addressList.ts
var ADDRESSES_QUERY = (
  /* GraphQL */
  `
  query Addresses(
    $scope: AddressListScope
    $entityId: String
    $includeGlobal: Boolean
    $limit: Int
    $cursor: String
    $resolveScope: AddressScope
    $addressType: String
    $chainIds: [String!]
  ) {
    addresses(
      scope: $scope
      entityId: $entityId
      includeGlobal: $includeGlobal
      limit: $limit
      cursor: $cursor
      resolveScope: $resolveScope
      addressType: $addressType
      chainIds: $chainIds
    ) {
      items {
        row { ${ADDRESS_ROW_FIELDS} }
        catalog { ${resolvedBundleFields(RESOLVED_HIT_CORE_FIELDS)} }
      }
      count
      nextCursor
    }
  }
`
);
var LIST_SCOPE_ENUM = {
  tenant: "TENANT",
  entity: "ENTITY"
};
function graphqlListScope(scope) {
  return LIST_SCOPE_ENUM[scope ?? "tenant"] ?? null;
}
function mapAddressPage(data) {
  const page = data.addresses;
  const addresses = (page?.items ?? []).map((item) => mapAddressRow(item.row));
  return {
    addresses,
    next_cursor: page?.nextCursor ?? null,
    // The SDL types `count` non-null, so the fallback is belt-and-braces — but a
    // 0 reported next to visible rows would make the table contradict its own
    // header, which is worth one `??`.
    count: page?.count ?? addresses.length
  };
}
function overlayKey(chainId, address) {
  return `${(chainId ?? "").toLowerCase()}::${address.toLowerCase()}`;
}
function mapAddressListOverlays(data) {
  const byKey = /* @__PURE__ */ new Map();
  const byAddress = /* @__PURE__ */ new Map();
  const rowByKey = /* @__PURE__ */ new Map();
  const rowByAddress = /* @__PURE__ */ new Map();
  for (const item of data.addresses?.items ?? []) {
    if (!item.catalog) continue;
    const row = mapAddressRow(item.row);
    const address = item.catalog.address || row.address;
    if (!address) continue;
    const lowered = address.toLowerCase();
    const key = overlayKey(item.catalog.chainId ?? row.chain, address);
    const overlays = mapBundle(item.catalog);
    byKey.set(key, overlays);
    byAddress.set(lowered, overlays);
    rowByKey.set(key, row);
    rowByAddress.set(lowered, row);
  }
  return { byKey, byAddress, rowByKey, rowByAddress };
}
async function graphqlListAddresses(client, query = {}, opts) {
  return (await graphqlListAddressesWithOverlays(client, query, opts)).page;
}
async function graphqlListAddressesWithOverlays(client, query = {}, opts, resolveScope) {
  const scope = graphqlListScope(query.scope);
  if (!scope) throw new Error(`address list scope "${query.scope}" has no GraphQL equivalent`);
  const data = await client.request(
    ADDRESSES_QUERY,
    {
      scope,
      entityId: query.entity_id,
      includeGlobal: query.include_global,
      limit: query.limit,
      cursor: query.cursor,
      addressType: query.address_type || void 0,
      // Absent, not `[]`: an empty list reaches the server as a filter no chain
      // satisfies, which would answer "no addresses" for a cleared facet.
      chainIds: query.chain_ids?.length ? [...query.chain_ids] : void 0,
      // `USER` is the caller's own view, which is what omitting it already means.
      resolveScope: resolveScope && resolveScope !== "USER" ? resolveScope : void 0
    },
    opts
  );
  return { page: mapAddressPage(data), overlays: mapAddressListOverlays(data) };
}

// src/graphql/operations/transactions.ts
var LATEST_TRANSACTIONS_QUERY = (
  /* GraphQL */
  `
  query LatestTransactions($limit: Int, $cursor: String) {
    latestTransactions(limit: $limit, cursor: $cursor) {
      items {
        canonicalTxId
        chainId
        blockTimestamp
        entities {
          id
          name
        }
        tags {
          catalogId
          name
          color
          logo
        }
        labels {
          catalogId
          name
          color
          logo
        }
        groups {
          catalogId
          name
          color
          logo
        }
      }
      nextCursor
    }
  }
`
);
var clean3 = (v) => v == null ? void 0 : v;
function mapRef2(ref) {
  return {
    catalogId: ref.catalogId,
    name: clean3(ref.name),
    color: clean3(ref.color),
    logo: clean3(ref.logo)
  };
}
function mapItem(item) {
  return {
    canonicalTxId: item.canonicalTxId,
    chainId: item.chainId,
    blockTimestamp: item.blockTimestamp,
    entities: (item.entities ?? []).map((e) => ({ id: e.id, name: clean3(e.name) })),
    tags: (item.tags ?? []).map(mapRef2),
    labels: (item.labels ?? []).map(mapRef2),
    groups: (item.groups ?? []).map(mapRef2)
  };
}
function mapLatestTransactions(data) {
  const feed = data.latestTransactions ?? { items: [], nextCursor: null };
  return {
    items: (feed.items ?? []).map(mapItem),
    nextCursor: clean3(feed.nextCursor)
  };
}
async function fetchLatestTransactions(client, args = {}, opts) {
  const data = await client.request(
    LATEST_TRANSACTIONS_QUERY,
    { limit: args.limit, cursor: args.cursor },
    opts
  );
  return mapLatestTransactions(data);
}

// src/graphql/operations/transaction.ts
var TRANSACTION_HEADER_QUERY = (
  /* GraphQL */
  `
  query TransactionHeader($canonicalTxId: ID, $chainId: String, $txHash: String) {
    transaction(canonicalTxId: $canonicalTxId, chainId: $chainId, txHash: $txHash) {
      canonicalTxId
      chainId
      txHash
      blockNumber
      blockHash
      blockTimestamp
      finality
      saved
      hasBody
      enrichmentStatus
      runId
      notes
    }
  }
`
);
var clean4 = (v) => v == null ? void 0 : v;
function mapTransactionHeader(data) {
  const t = data.transaction;
  if (!t) return null;
  return {
    canonicalTxId: t.canonicalTxId,
    chainId: t.chainId,
    txHash: clean4(t.txHash),
    blockNumber: clean4(t.blockNumber),
    blockHash: clean4(t.blockHash),
    blockTimestamp: clean4(t.blockTimestamp),
    finality: clean4(t.finality),
    saved: Boolean(t.saved),
    hasBody: Boolean(t.hasBody),
    enrichmentStatus: clean4(t.enrichmentStatus),
    runId: clean4(t.runId),
    notes: clean4(t.notes)
  };
}
async function fetchTransactionHeader(client, args, opts) {
  const data = await client.request(
    TRANSACTION_HEADER_QUERY,
    { canonicalTxId: args.canonicalTxId, chainId: args.chainId, txHash: args.txHash },
    opts
  );
  return mapTransactionHeader(data);
}

// src/graphql/operations/assignments.ts
var CATALOG_KIND_ENUM = {
  entity: "ENTITY",
  tag: "TAG",
  label: "LABEL",
  group: "GROUP"
};
function toCatalogKindEnum(kind) {
  const enumValue = CATALOG_KIND_ENUM[kind?.toLowerCase?.() ?? ""];
  if (!enumValue) throw new Error(`catalog kind "${kind}" is not a GraphQL CatalogKind`);
  return enumValue;
}
var clean5 = (v) => v == null ? void 0 : v;
function toMemberTier(raw) {
  const value = raw?.toLowerCase?.();
  return value === "core" || value === "derived" ? value : null;
}
function toTierEnum(tier) {
  return tier == null ? void 0 : tier.toUpperCase();
}
function mapAssignment(row) {
  return {
    id: row.id,
    scope: row.scope,
    scope_target_id: row.scopeTargetId,
    chain_id: row.chainId ?? null,
    catalog_kind: row.catalogKind,
    catalog_id: row.catalogId,
    user_id: row.userId ?? null,
    is_human_approved: row.isHumanApproved,
    created_at: clean5(row.createdAt),
    confidence: clean5(row.confidence),
    explanation: row.explanation ?? []
    // Server-projected subset; the editor reads kind/id/user_id/id. Remaining
    // CatalogAssignmentDTO fields (ABAC carriers, shadow keys) aren't part of
    // the lean overlay view and are left to the DTO's own defaults.
  };
}
function mapPrimaryPointer(row) {
  return {
    scope: "entity",
    scope_target_id: row.entityId,
    catalog_kind: "address",
    catalog_id: row.catalogId,
    chain_id: row.chainId
  };
}
function mapEntityMapping(row) {
  if (!row) return null;
  return {
    chain: row.chainId,
    address: row.address,
    entity_id: row.entityId,
    role: row.role ?? null,
    // `CORE` -> `core`: the schema declares its enums UPPER_SNAKE, the storage
    // vocabulary is lower-case, and the matrix compares against the latter.
    tier: toMemberTier(row.tier),
    confidence: row.confidence,
    valid_from: row.validFrom ?? null,
    valid_to: row.validTo ?? null,
    // Preserved as `undefined` when absent rather than coerced to a boolean:
    // "the read did not judge the window" is not "superseded", and a view that
    // conflated them would draw every current member as retired.
    active: row.active ?? void 0,
    is_human_approved: row.isHumanApproved,
    source: row.source ?? null,
    explanation: row.explanation ?? []
  };
}
var ADDRESS_ASSIGNMENTS_QUERY = (
  /* GraphQL */
  `
  query AddressAssignments($address: String!, $chainId: String!, $limit: Int, $cursor: String) {
    addressAssignments(address: $address, chainId: $chainId, limit: $limit, cursor: $cursor) {
      items { id scope scopeTargetId chainId catalogKind catalogId userId isHumanApproved createdAt confidence explanation }
      count
      nextCursor
    }
  }
`
);
var ADDRESS_ENTITY_MAPPING_QUERY = (
  /* GraphQL */
  `
  query AddressEntityMappingQuery($address: String!, $chainId: String!, $asOf: Int) {
    addressEntityMapping(address: $address, chainId: $chainId, asOf: $asOf) {
      entityId chainId address role tier confidence validFrom validTo active isHumanApproved source explanation
    }
  }
`
);
var ASSIGNMENT_FIELDS = "id scope scopeTargetId chainId catalogKind catalogId userId isHumanApproved createdAt confidence explanation";
var ASSIGN_ADDRESS_CATALOG_MUTATION = (
  /* GraphQL */
  `
  mutation AssignAddressCatalog($input: AssignCatalogInput!) {
    assignAddressCatalog(input: $input) { ${ASSIGNMENT_FIELDS} }
  }
`
);
var UNASSIGN_ADDRESS_CATALOG_MUTATION = (
  /* GraphQL */
  `
  mutation UnassignAddressCatalog($input: UnassignCatalogInput!) {
    unassignAddressCatalog(input: $input) { ok changed }
  }
`
);
var SET_ADDRESS_PRIMARY_MUTATION = (
  /* GraphQL */
  `
  mutation SetAddressPrimary($input: SetPrimaryInput!) {
    setAddressPrimary(input: $input) { entityId chainId catalogId }
  }
`
);
var CLEAR_ADDRESS_PRIMARY_MUTATION = (
  /* GraphQL */
  `
  mutation ClearAddressPrimary($input: ClearPrimaryInput!) {
    clearAddressPrimary(input: $input) { ok changed }
  }
`
);
var ENTITY_MAPPING_FIELDS = "entityId chainId address role tier confidence validFrom validTo active isHumanApproved source explanation";
var PUT_ADDRESS_ENTITY_MAPPING_MUTATION = (
  /* GraphQL */
  `
  mutation PutAddressEntityMapping($input: PutEntityMappingInput!) {
    putAddressEntityMapping(input: $input) { ${ENTITY_MAPPING_FIELDS} }
  }
`
);
var DELETE_ADDRESS_ENTITY_MAPPING_MUTATION = (
  /* GraphQL */
  `
  mutation DeleteAddressEntityMapping($input: DeleteEntityMappingInput!) {
    deleteAddressEntityMapping(input: $input) { ok changed }
  }
`
);
var ENTITY_MEMBERSHIPS_QUERY = (
  /* GraphQL */
  `
  query EntityMemberships(
    $entityId: ID!
    $limit: Int
    $cursor: String
    $preferCore: Boolean
    $addressType: String
    $chainIds: [String!]
  ) {
    entityMemberships(
      entityId: $entityId
      limit: $limit
      cursor: $cursor
      preferCore: $preferCore
      addressType: $addressType
      chainIds: $chainIds
    ) {
      items { ${ENTITY_MAPPING_FIELDS} }
      count
      nextCursor
      primaryByChain
      memberTotal
      asOf
      lane
      truncated
    }
  }
`
);
var PUT_ADDRESS_ENTITY_MEMBERSHIPS_MUTATION = (
  /* GraphQL */
  `
  mutation PutAddressEntityMemberships($input: PutEntityMembershipsInput!) {
    putAddressEntityMemberships(input: $input) {
      count
      failed
      results {
        chainId
        address
        ok
        error
        mapping { ${ENTITY_MAPPING_FIELDS} }
      }
    }
  }
`
);
async function graphqlListAssignments(client, args, opts) {
  const data = await client.request(
    ADDRESS_ASSIGNMENTS_QUERY,
    { address: args.address, chainId: args.chainId, limit: args.limit, cursor: args.cursor },
    opts
  );
  return (data.addressAssignments?.items ?? []).map(mapAssignment);
}
async function graphqlAttachAssignment(client, args, opts) {
  const { input } = args;
  const data = await client.request(
    ASSIGN_ADDRESS_CATALOG_MUTATION,
    {
      input: omitUndefined({
        address: args.address,
        chainId: args.chainId,
        kind: toCatalogKindEnum(input.catalog_kind),
        catalogId: input.catalog_id,
        userScoped: input.user_scoped ?? true,
        visibleToUserIds: input.visible_to_user_ids,
        visibleToTeamIds: input.visible_to_team_ids,
        confidence: input.confidence,
        reason: input.reason,
        evidenceUrl: input.evidence_url,
        validFrom: input.valid_from,
        validTo: input.valid_to
      })
    },
    opts
  );
  return mapAssignment(data.assignAddressCatalog);
}
function omitUndefined(input) {
  const out = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== void 0) out[key] = value;
  }
  return out;
}
async function graphqlDetachAssignment(client, args, opts) {
  await client.request(
    UNASSIGN_ADDRESS_CATALOG_MUTATION,
    {
      input: omitUndefined({
        address: args.address,
        chainId: args.chainId,
        kind: toCatalogKindEnum(args.input.catalog_kind),
        catalogId: args.input.catalog_id,
        userScoped: args.input.user_scoped
      })
    },
    opts
  );
}
async function graphqlSetPrimary(client, args, opts) {
  const data = await client.request(
    SET_ADDRESS_PRIMARY_MUTATION,
    { input: { entityId: args.entityId, address: args.address, chainId: args.chainId } },
    opts
  );
  return mapPrimaryPointer(data.setAddressPrimary);
}
async function graphqlClearPrimary(client, args, opts) {
  await client.request(
    CLEAR_ADDRESS_PRIMARY_MUTATION,
    { input: { entityId: args.entityId, chainId: args.chainId } },
    opts
  );
}
async function graphqlGetEntityMapping(client, args, opts) {
  const data = await client.request(
    ADDRESS_ENTITY_MAPPING_QUERY,
    { address: args.address, chainId: args.chainId, asOf: args.asOf },
    opts
  );
  return mapEntityMapping(data.addressEntityMapping);
}
async function graphqlPutEntityMapping(client, args, opts) {
  const { input } = args;
  const data = await client.request(
    PUT_ADDRESS_ENTITY_MAPPING_MUTATION,
    {
      input: {
        address: args.address,
        chainId: args.chainId,
        entityId: input.entity_id,
        role: input.role,
        tier: toTierEnum(input.tier),
        confidence: input.confidence,
        validFrom: input.valid_from,
        validTo: input.valid_to
      }
    },
    opts
  );
  return mapEntityMapping(data.putAddressEntityMapping);
}
async function graphqlDeleteEntityMapping(client, args, opts) {
  const data = await client.request(
    DELETE_ADDRESS_ENTITY_MAPPING_MUTATION,
    { input: { address: args.address, chainId: args.chainId, entityId: args.entityId } },
    opts
  );
  return data.deleteAddressEntityMapping?.changed ?? 0;
}
async function graphqlListEntityMemberships(client, args, opts) {
  const data = await client.request(
    ENTITY_MEMBERSHIPS_QUERY,
    {
      entityId: args.entity_id,
      limit: args.limit,
      cursor: args.cursor,
      preferCore: args.prefer_core,
      addressType: args.address_type,
      // `chains` is NOT sent here: on this query it scopes the headline pointer,
      // and GraphQL derives that coverage itself. This one narrows the rows.
      chainIds: args.chain_ids?.length ? [...args.chain_ids] : void 0
    },
    opts
  );
  const page = data.entityMemberships;
  const mappings = (page?.items ?? []).map(mapEntityMapping).filter((m) => m != null);
  return {
    mappings,
    next_cursor: page?.nextCursor ?? null,
    count: page?.count ?? mappings.length,
    primary_by_chain: mapPrimaryByChain(page?.primaryByChain),
    // `null` and absent both mean "nobody has counted this entity", which the
    // caller must render as unknown rather than as zero.
    member_total: page?.memberTotal ?? null,
    lane: toLane(page?.lane),
    truncated: page?.truncated ?? false
  };
}
function toLane(value) {
  return String(value ?? "").toLowerCase() === "core" ? "core" : "all";
}
function mapPrimaryByChain(raw) {
  if (raw == null) return {};
  let source = raw;
  if (typeof raw === "string") {
    try {
      source = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof source !== "object" || source === null || Array.isArray(source)) return {};
  const out = {};
  for (const [chain, address] of Object.entries(source)) {
    if (chain && typeof address === "string" && address) out[chain] = address;
  }
  return out;
}
async function graphqlPutEntityMemberships(client, args, opts) {
  const data = await client.request(
    PUT_ADDRESS_ENTITY_MEMBERSHIPS_MUTATION,
    {
      input: omitUndefined({
        entityId: args.entity_id,
        role: args.role,
        tier: toTierEnum(args.tier),
        confidence: args.confidence,
        members: args.members.map(
          (m) => omitUndefined({
            address: m.address,
            chainId: m.chain,
            role: m.role,
            tier: toTierEnum(m.tier),
            confidence: m.confidence,
            validFrom: m.valid_from,
            validTo: m.valid_to
          })
        )
      })
    },
    opts
  );
  const out = data.putAddressEntityMemberships;
  const results = (out?.results ?? []).map((r) => ({
    chain: r.chainId,
    address: r.address,
    ok: r.ok,
    mapping: mapEntityMapping(r.mapping),
    error: r.error ?? null
  }));
  return {
    results,
    count: out?.count ?? results.filter((r) => r.ok).length,
    failed: out?.failed ?? results.filter((r) => !r.ok).length,
    mappings: results.map((r) => r.mapping).filter((m) => m != null)
  };
}

export { ADDRESSES_QUERY, ADDRESS_ASSIGNMENTS_QUERY, ADDRESS_DETAIL_QUERY, ADDRESS_ENTITY_MAPPING_QUERY, ADDRESS_OVERLAYS_QUERY, ADDRESS_ROW_FIELDS, ADDRESS_SCOPES, ASSIGN_ADDRESS_CATALOG_MUTATION, CATALOG_ITEMS_QUERY, CATALOG_RELATED_QUERY, CLEAR_ADDRESS_PRIMARY_MUTATION, DELETE_ADDRESS_ENTITY_MAPPING_MUTATION, ENTITY_MEMBERSHIPS_QUERY, GraphQLClient, GraphQLRequestError, LATEST_TRANSACTIONS_QUERY, PUT_ADDRESS_ENTITY_MAPPING_MUTATION, PUT_ADDRESS_ENTITY_MEMBERSHIPS_MUTATION, RELATED_SEED_KINDS, RESOLVED_ENTITY_FIELDS, RESOLVED_HIT_CORE_FIELDS, RESOLVED_HIT_FIELDS, SET_ADDRESS_PRIMARY_MUTATION, TRANSACTION_HEADER_QUERY, UNASSIGN_ADDRESS_CATALOG_MUTATION, catalogKindToGraphql, fetchAddressOverlays, fetchCatalogItems, fetchCatalogRelated, fetchLatestTransactions, fetchTransactionHeader, graphqlAttachAssignment, graphqlClearPrimary, graphqlDeleteEntityMapping, graphqlDetachAssignment, graphqlFetchAddressDetail, graphqlGetEntityMapping, graphqlListAddresses, graphqlListAddressesWithOverlays, graphqlListAssignments, graphqlListEntityMemberships, graphqlListScope, graphqlPutEntityMapping, graphqlPutEntityMemberships, graphqlSetPrimary, mapAddressDetail, mapAddressListOverlays, mapAddressOverlays, mapAddressPage, mapAddressRow, mapAddressRowScope, mapAssignment, mapBundle, mapCatalogItemToRow, mapCatalogItems, mapEntity, mapEntityMapping, mapLatestTransactions, mapPrimaryPointer, mapRelatedGraph, mapTransactionHeader, relatedChildren, relatedOfKind, resolvedBundleFields, toAddressScope, toCatalogKindEnum };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map